import './styles.scss'
import image from '../../../Assets/Images/quote-modal-img.jpeg'
import { CSSTransition } from 'react-transition-group'
import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import Input from '../../Input'
import InputArea from '../../InputArea'
import CustomButton from '../../CustomButton'
import { useForm } from '../../../Hooks/useForm'
import { ROUTES } from '../../../constants'
import emailjs from '@emailjs/browser'

const templateId = process.env.REACT_APP_EMAILJS_QUOTE_TEMPLATE_ID
const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID
const publicId = process.env.REACT_APP_EMAILJS_PUBLIC_KEY

const Req = () => <span className="req">*</span>

const QuoteForm = ({ showForm, handleClose, ...props }) => {
  const formRef = useRef(null)
  const location = useLocation()
  const [status, setStatus] = useState(null) // null | 'success' | 'error'

  const defaultService =
    location.pathname === ROUTES.services ? location.state?.service || '' : ''

  const handleClick = (e) => e?.stopPropagation()

  const { form, reset, onChange } = useForm({
    name: '',
    company: '',
    email: '',
    phone: '',
    service_type: defaultService,
    origin: '',
    destination: '',
    weight_value: '',
    weight_unit: 'kg',
    msg: '',
    loading: false,
  })

  const handleChange = (e) => onChange(e.target.name, e.target.value)

  useEffect(() => {
    return reset
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync service_type when overlay opens on a service page
  useEffect(() => {
    if (showForm && defaultService) {
      onChange('service_type', defaultService)
    }
  }, [showForm]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault()
    onChange('loading', true)
    setStatus(null)

    const payload = {
      ...form,
      estimated_weight: form.weight_value
        ? `${form.weight_value} ${form.weight_unit}`
        : '',
    }

    try {
      await emailjs.send(serviceId, templateId, payload, publicId)
      reset()
      setStatus('success')
    } catch (error) {
      setStatus('error')
    }

    onChange('loading', false)
  }

  return (
    <CSSTransition
      in={showForm}
      nodeRef={formRef}
      timeout={300}
      unmountOnExit
      classNames="quote-form"
    >
      <div
        ref={formRef}
        onClick={handleClick}
        className="quote-form"
        {...props}
      >
        <div className="quote-form__image">
          <img src={image} alt="" />
        </div>

        <div className="quote-form__content">
          <div className="quote-form__header">
            <div className="quote-form__header__text">
              <h5 className="title">Get A Free Quote</h5>
              <p className="subtitle">
                Fields marked <Req /> are required. We respond within 24 hours.
              </p>
            </div>
            <button
              className="close-btn"
              type="button"
              onClick={handleClose}
              aria-label="Close"
            >
              <FontAwesomeIcon icon={solid('xmark')} />
            </button>
          </div>

          {status === 'success' ? (
            <div className="form-success">
              <div className="form-success__icon">
                <FontAwesomeIcon icon={solid('circle-check')} />
              </div>
              <h3>Quote Request Sent!</h3>
              <p>
                Thank you — our team will review your shipment details and get
                back to you within 24 hours.
              </p>
              <button className="form-success__close" onClick={handleClose}>
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {status === 'error' && (
                <div className="form-error">
                  <FontAwesomeIcon icon={solid('triangle-exclamation')} />{' '}
                  Something went wrong — please try again or email us at{' '}
                  <strong>business@sterlinglogistics.us</strong>
                </div>
              )}

              <div className="form-section">
                <span className="form-section__label">About You</span>

                <div className="form-row">
                  <Input
                    title={<>Full Name <Req /></>}
                    required
                    placeholder="John Doe"
                    value={form.name}
                    onChange={handleChange}
                    name="name"
                  />
                  <Input
                    title="Company Name"
                    placeholder="Your Company Ltd."
                    value={form.company}
                    onChange={handleChange}
                    name="company"
                  />
                </div>

                <div className="form-row">
                  <Input
                    title={<>Email Address <Req /></>}
                    type="email"
                    required
                    placeholder="john@example.com"
                    value={form.email}
                    onChange={handleChange}
                    name="email"
                  />
                  <Input
                    title="Phone Number"
                    type="tel"
                    placeholder="+1 (555) 000-0000"
                    value={form.phone}
                    onChange={handleChange}
                    name="phone"
                  />
                </div>
              </div>

              <div className="form-section">
                <span className="form-section__label">Your Shipment</span>

                <div className="form-field">
                  <div className="label">Service Type <Req /></div>
                  <select
                    name="service_type"
                    value={form.service_type}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select a service...</option>
                    <option value="Air Freight">Air Freight</option>
                    <option value="Ocean Freight">Ocean Freight</option>
                    <option value="Cargo Transportation">Cargo Transportation</option>
                    <option value="Packaging and Storage">Packaging and Storage</option>
                  </select>
                </div>

                <div className="form-row">
                  <Input
                    title={<>Origin / Pickup <Req /></>}
                    required
                    placeholder="City, Country"
                    value={form.origin}
                    onChange={handleChange}
                    name="origin"
                  />
                  <Input
                    title={<>Destination <Req /></>}
                    required
                    placeholder="City, Country"
                    value={form.destination}
                    onChange={handleChange}
                    name="destination"
                  />
                </div>

                <div className="form-field">
                  <div className="label">Estimated Weight</div>
                  <div className="weight-input">
                    <input
                      type="number"
                      name="weight_value"
                      placeholder="0"
                      min="0"
                      value={form.weight_value}
                      onChange={handleChange}
                    />
                    <select
                      name="weight_unit"
                      value={form.weight_unit}
                      onChange={handleChange}
                    >
                      <option value="kg">kg</option>
                      <option value="lbs">lbs</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <span className="form-section__label">Additional Notes</span>
                <InputArea
                  rows={3}
                  placeholder="Special handling, hazardous materials, preferred dates..."
                  value={form.msg}
                  onChange={handleChange}
                  name="msg"
                />
              </div>

              <CustomButton
                loading={form.loading}
                otherClassName="submit"
                type="submit"
                label="REQUEST QUOTE"
              />
            </form>
          )}
        </div>
      </div>
    </CSSTransition>
  )
}

export default QuoteForm
